<template>
  <div class="list-control d-flex">
    <div v-if="isTablet || isMobile" v-click-outside="close" class="list-control__mobile d-flex flex-column justify-content-center">
      <div class="d-flex justify-content-end align-items-center">
        <slot name="icons" />
        <span
          class="cursor-pointer filter-icon"
          @click="open = !open"
        >
          <font-awesome-layers>
            <font-awesome-icon :icon="filterIcon" size="lg" />
          </font-awesome-layers>
          <font-awesome-icon v-if="selectedFilters > 0" icon="circle" class="text-success filter-active" transform="shrink-6" />
        </span>
      </div>
      <transition-expand-side v-if="open" :expand-direction="'expand-side-left'">
        <div class="list-control__mobile__pane d-flex flex-column">
          <span class="close-pane ml-4 mb-md-4 cursor-pointer" @click="open = !open">
            <font-awesome-icon :icon="['far', 'times-circle']" size="2x" />
          </span>
          <div class="list-control__mobile__pane__scrollable d-flex flex-column">
            <div class="d-flex flex-column">
              <slot name="contextual" />
              <template v-if="coverageOptions">
                <select-accordion name="Coverage" :value="coverageText" class="list-control__mobile__pane__item" :accordion="'parent-accordion'">
                  <validation-observer ref="observer" v-slot="{ handleSubmit }">
                    <template v-for="(option, key) in coverageOptions">
                      <div v-if="option.input === false"
                           :key="key"
                           class="coverage-item text-left inner"
                      >
                        <span class="p-3" @click="setCoverage(option)">{{ option.text }}</span>
                      </div>
                      <b-form v-else :key="key" class="coverage-item text-left inner" @submit.prevent="handleSubmit(setCoverage(option, true))">
                        <div class="px-3">
                          <b-input-group>
                            <span class="mb-2">{{ option.text }}</span>
                          </b-input-group>
                          <validation-provider
                            v-slot="{validated, errors, dirty, valid}"
                            rules="required|postcode"
                          >
                            <b-input-group>
                              <b-form-input v-model="postcode" placeholder="e.g. LE16 7QU" @click.prevent.stop />
                              <b-input-group-append>
                                <b-button size="sm" variant="primary" @click.prevent.stop="setCoverage(option, true)">
                                  {{ $t('Find') }}
                                </b-button>
                              </b-input-group-append>
                            </b-input-group>
                            <b-form-invalid-feedback :state="valid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </validation-provider>
                        </div>
                      </b-form>
                    </template>
                  </validation-observer>
                </select-accordion>
              </template>
              <template v-if="sortFields !== null">
                <select-accordion
                  v-model="sortBy"
                  :options="sortOptions"
                  :name="$t('Sort by')"
                  class="text-left list-control__mobile__pane__item"
                  :accordion="'parent-accordion'"
                />
              </template>
              <template v-if="filterOptions !== undefined">
                <select-accordion
                  :name="$t('Filter by')"
                  :value="selectedFilters > 0 ? selectedFilters + ' active' : 'None'"
                  class="list-control__mobile__pane__item"
                  :accordion="'parent-accordion'"
                >
                  <div class="text-center p-1">
                    <b-button class="text-underline" variant="link" @click="clearFilters">
                      {{ $t('Clear filters') }}
                    </b-button>
                  </div>
                  <select-accordion
                    v-for="filter in filterOptions"
                    :key="filter.key"
                    :options="filter.values"
                    :name="filterOptionlabel(filter.label, filter.key)"
                    :value="filters[filter.key]"
                    :null-value="true"
                    :accordion="'filter-accordion'"
                    class="text-left inner"
                    @input="updateFilter(filter.key, $event)"
                  />
                  <form v-if="search" @submit.prevent.stop="updateFilter('search', searchValue)">
                    <div class="d-flex justify-content-start align-items-center p-3">
                      <span class="font-weight-bold d-block  py-2 text-center mr-1">{{ $t('Search:') }} </span>
                      <b-input-group :label="$t('Search:')">
                        <b-form-input id="search" v-model="searchValue" type="text" @click.prevent.stop />
                        <b-input-group-append>
                          <b-button variant="primary" class="py-0 px-2" @click="updateFilter('search', searchValue)">
                            <font-awesome-icon icon="search" size="xs" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </div>
                  </form>
                </select-accordion>
              </template>
              <a v-if="download && download.length > 0 && !isTablet && !isMobile"
                 class="ml-1 btn btn-empty export pr-0"
                 :href="download"
                 :title="$t('Download past 12 months leads')"
              >
                <font-awesome-icon icon="download" /> <span class="export__text ml-1">{{ $t('Export My Leads') }}</span>
              </a>
            </div>
            <div v-if="showArchiveToggle" class="mt-3 d-flex align-items-center justify-content-center">
              <b-form-checkbox v-model="showArchived" class="custom-switch-text float-left" switch>
                <span class="hide" v-html="$t('Show Archived')" /><span class="show" v-html="$t('Hide Archived')" />
              </b-form-checkbox>
              <div id="archived-switch-tooltip" class="ml-3 archived-switch-info" @click.prevent.stop>
                <font-awesome-icon class="float-left " icon="question-circle" />
              </div>
              <b-tooltip target="archived-switch-tooltip" triggers="hover">
                <p v-html="$t('archivedTooltip')" />
                <p class="mb-0">
                  {{ $t("Click into 'Full View' to use the archive feature.") }}
                </p>
              </b-tooltip>
            </div>
          </div>
        </div>
      </transition-expand-side>
    </div>
    <div v-else class="d-flex list-control__desktop">
      <div class="list-icons d-flex">
        <slot name="icons" />
      </div>
      <div class="d-flex justify-content-end filter">
        <slot name="contextual" />
        <div v-if="showArchiveToggle" class="filter-item separator ml-1">
          <b-form-checkbox v-model="showArchived" class="custom-switch-text m-auto" switch>
            <span class="hide" v-html="$t('Show Archived')" /><span class="show" v-html="$t('Hide Archived')" />
          </b-form-checkbox>
        </div>
        <template v-if="coverageOptions">
          <custom-select ref="coverage-selector" name="Coverage" :value="coverageText">
            <validation-observer ref="observer" v-slot="{ handleSubmit }">
              <template v-for="(option, key) in coverageOptions">
                <div
                  v-if="option.input === false"
                  :key="key"
                  class="coverage-item"
                  @click="setCoverage(option)"
                >
                  <span>{{ option.text }}</span>
                </div>
                <b-form v-else :key="key" class="coverage-item" @submit.prevent="handleSubmit(setCoverage(option, true))">
                  <b-input-group>
                    <span class="mb-2">{{ option.text }}</span>
                  </b-input-group>
                  <validation-provider
                    v-slot="{validated, errors, dirty, valid}"
                    rules="required|postcode"
                  >
                    <b-input-group>
                      <b-form-input v-model="postcode" :state="(dirty || validated) ? (!!valid) : null" />
                      <b-input-group-append>
                        <b-button size="sm" variant="primary" @click.prevent.stop="setCoverage(option, true)">
                          {{ $t('Find') }}
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback :state="valid">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-form>
              </template>
            </validation-observer>
          </custom-select>
        </template>
        <template v-if="filterOptions !== undefined">
          <custom-select :name="$t('Filter by')" :tooltipPlacement="setTooltipPlacement" :value="selectedFilters > 0 ? selectedFilters + ' active' : 'None'">
            <div class="text-center p-1">
              <b-button class="text-underline" variant="link" @click="clearFilters">
                {{ $t('Clear filters') }}
              </b-button>
            </div>
            <select-accordion
              v-for="filter in filterOptions"
              :key="filter.key"
              :options="filter.values"
              :name="filter.label"
              :value="filters[filter.key]"
              :null-value="true"
              :accordion="'filter-accordion'"
              @input="updateFilter(filter.key, $event)"
            />
            <form v-if="search" @submit.prevent.stop="updateFilter('search', searchValue)">
              <div class="d-flex justify-content-start align-items-center p-3">
                <span class="font-weight-bold d-block  py-2 text-center mr-1">{{ $t('Search:') }} </span>
                <b-input-group :label="$t('Search:')">
                  <b-form-input id="search" v-model="searchValue" type="text" />
                  <b-input-group-append>
                    <b-button variant="primary" class="py-0 px-2" @click="updateFilter('search', searchValue)">
                      <font-awesome-icon icon="search" size="xs" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </form>
          </custom-select>
        </template>
        <template v-if="sortFields !== null && sortFields !== undefined">
          <custom-select v-model="sortBy" class="sort-by" :options="sortOptions" :name="$t('Sort by')" />
        </template>
        <div v-if="isMyLeadsPage" class="filter-item export">
          <a
            class="ml-1 btn btn-outline-secondary btn-sm text-nowrap btn text-nowrap mr-xl-1 btn-outline-secondary btn-sm"
            :href="download"
            :class="{'disabled': !(download && download.length > 0) }"
            :title="$t('Download past 12 months leads')"
            @click="displayExportNotification"
          >
            <font-awesome-icon class="text-success" icon="download" /> <span class="export__text ml-1">{{ $t('Export My Leads') }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SHOW_ARCHIVED_LEADS} from "@/store/leads/mutation-types";
import {CLEAR_FILTER, UPDATE_FILTER} from "@/store/mutation-types";
import CustomSelect from "@/components/form/elements/CustomSelect";
import SelectAccordion from "@/components/form/elements/SelectAccordion";
import userDevice from "@/imports/mixins/userDevice";
import notification from "@/imports/mixins/notification";
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import TransitionExpandSide from "@/components/animation/TransitionExpandSide";

const POSTCODE = 4;
export default {
  name: "ResultListControl",
  components: {SelectAccordion, CustomSelect, ValidationProvider, ValidationObserver, TransitionExpandSide},
  mixins: [userDevice, notification],
  props: {
    sortFields: {
      type: Array,
      default: null
    },
    filterOptions: {
      type: Array,
      default: undefined
    },
    download: {
      type: String,
      default: null,
    },
    showArchiveToggle: {
      type: Boolean,
    },
    storeKey: {
      type: String,
      default: 'global',
    },
    showStatusKey: {
      type: Boolean,
    },
    coverageOptions: {
      type: Array,
      default: undefined
    },
    defaultOrder: {
      type: String,
      default: null
    },
    filterIcon: {
      type: String,
      default: 'filter',
    },
    search: {
      type: Boolean,
      default: false,
    }
  },
  data: function () {
    return {
      sortBy: null,
      filters: this.getInitial(),
      postcode: null,
      open: false,
      searchValue: null,
    }
  },
  computed: {
    showArchived: {
      get: function () {
        return this.$store.state.leads.SHOW_ARCHIVED_LEADS;
      },
      set: function (value) {
        this.$store.commit(SHOW_ARCHIVED_LEADS, value);
      }
    },
    sortOptions: function () {
      let filter = [];
      this.sortFields.forEach((column) => {
        if (column.canSort) {
          if (column.customSortLabels) {
            filter.push(
              {text: column.customSortLabels[0], value: column.key + ((column.invertOrder) ? '-desc' : '-asc')},
              {text: column.customSortLabels[1], value: column.key + ((column.invertOrder) ? '-asc' : '-desc')},
            );
          } else {
            filter.push(...this.getColumnSortOptions(column));
          }
        }
      });
      return filter;
    },
    storeFilters: {
      get: function () {
        return this.$store.getters.getFilter(this.storeKey);
      },
      set: function (value) {
        this.$store.commit(UPDATE_FILTER, {group: this.storeKey, filters: value});
      }
    },
    selectedFilters: function () {
      let selected = 0;
      const filterOptions = this.filterOptions;
      if (filterOptions !== undefined) {
        this.filterOptions.forEach((filter) => {
          if (Object.prototype.hasOwnProperty.call(this.storeFilters, filter.key)) {
            if (this.storeFilters[filter.key] !== null) {
              selected++;
            }
          }
        });
      }
      if (this.searchValue !== null && this.searchValue !== '') {
        selected++;
      }
      return selected;
    },
    coverageValue: function () {
      return (this.storeFilters.coverage) ?? 3;
    },
    coveragePostcode: function () {
      return (this.storeFilters.postcode) ?? null;
    },
    coverageText: function () {
      let option = this.coverageOptions.find((obj) => obj.value === this.coverageValue);
      if (option && option.input === true && option.value === POSTCODE) {
        return 'Postcode: ' + this.coveragePostcode;
      }
      return option.text;
    },
    isMyLeadsPage: function() {
      return this.$route.name === 'my-leads';
    },
    setTooltipPlacement: function() {
      return this.isMyLeadsPage ? 'bottom' : 'bottomleft' ;
    },
  },
  methods: {
    getInitial: function () {
      let filters = this.$store.getters.getFilter(this.storeKey);
      if (this.filterOptions !== undefined && Object.keys(filters).length === 0) {
        this.filterOptions.map(option => filters[option.key] = null);
      }
      return filters;
    },
    getColumnSortOptions(column) {
      if (column.key === null) {
        return [{text: column.label , value: column.key}]
      } else {
        return [
          {text: column.label + ' Asc', value: column.key + '-asc'},
          {text: column.label + ' Desc', value: column.key + '-desc'}
        ]
      }
    },
    clearFilters: function () {
      this.$store.commit(CLEAR_FILTER, this.storeKey);
      this.searchValue = null;
      this.filters = this.getInitial();
    },
    updateFilter: function (key, value) {
      this.storeFilters = {...this.filters, [key]: value};
      this.$emit('filter', this.filters);
    },
    setCoverage(option, usePostcode = false) {
      if (usePostcode) {
        this.$refs.observer.validate().then(success => {
          if (!success) {
            return;
          }
          let filters = {...this.filters, 'coverage': option.value};
          filters.postcode = this.postcode;
          this.storeFilters = filters;
          if(this.$refs['coverage-selector']) {
            this.$refs['coverage-selector'].close();
          }
          this.$emit('filter', this.filters);
        });
      } else {
        let filters = {...this.filters, 'coverage': option.value};
        this.postcode = null;
        delete filters.postcode;
        this.storeFilters = filters;
        if(this.$refs['coverage-selector']) {
          this.$refs['coverage-selector'].close();
        }
        this.$emit('filter', this.filters);
      }
    },
    displayExportNotification: function() {
      this.notify(
        this.$bvToast,
        'Generating export, please check your downloads',
        null,
        'success'
      );
    },
    close: function () {
      this.open = false;
    },
    filterOptionlabel: function(label, key) {
      if (key === 'qualification' && !this.isDesktop) {
        return 'Qualified';
      }
      return label;
    }
  },
  watch: {
    sortBy: function (value) {
      this.updateFilter('order', value);
      this.$emit('sort', value);
    },
    storeFilters: function () {
      this.filters = this.storeFilters;
    }
  },
  created: function() {
    if(this.defaultOrder && this.sortBy === null) {
      this.sortBy = this.defaultOrder;
    }
    this.$eb.$on('nav-state', state => {
      if (state === true) {
        this.open = false;
      }
    });
  },
  destroyed() {
    this.$eb.$off('nav-state');
  },
}
</script>

<style scoped lang="scss">

  .coverage-item {
    padding: 15px 20px;
    margin: 0;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background-color: $filter-item-hover;
    }
  }

  .text {
    &-warning {
      color: #fad767 !important;
    }

    &-success {
      color: #85d6b4 !important;
    }

    &-danger {
      color: #f08e8e !important;
    }

    &-info {
      color: $theme-secondary-blue-300 !important;
    }
  }

  .custom-switch-text {
    .custom-control-label {
      span {
        line-height: 1.1;
        top: 4px;
      }
    }
  }

  .export {
    margin-left: 1rem;

    &__text {
      font-size: $font-size-13;
    }
  }

  .list-icons {
    .svg-inline--fa {
      width: 0.8em;
      height: 0.8em;
      vertical-align: -0.05em;
    }
  }

  .archived-switch-info {
    width: 33px;
    height: 33px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .text-underline.btn-link {
    text-decoration: underline;
  }
</style>
